import { useEffect, useState } from "react";
import { getWastePercentReport } from "../../services";
import {
  WastePercentReport,
  WastePercentReportParams,
} from "../../types/waste-percent-report.type";

interface Answer {
  params: WastePercentReportParams;
  /** null when the request failed. */
  report: WastePercentReport | null;
}

/**
 * The report for `params`, asked again whenever they change and not asked at all while they are
 * null. Only the latest request may settle, and nothing is returned until the CURRENT params are
 * answered, so a new month or route never shows the previous filter's figures under it.
 * `params` must keep its identity between renders.
 */
export function useWastePercentReport(params: WastePercentReportParams | null) {
  const [answer, setAnswer] = useState<Answer>();

  useEffect(() => {
    if (params === null) return undefined;
    let isLatest = true;
    getWastePercentReport(params)
      .then((response) => {
        if (isLatest) setAnswer({ params, report: response.data.data });
      })
      .catch(() => {
        // The interceptor has already reported the failure; the page shows it failed rather
        // than keep the previous filter's figures on screen.
        if (isLatest) setAnswer({ params, report: null });
      });
    return () => {
      isLatest = false;
    };
  }, [params]);

  const current = params !== null && answer?.params === params ? answer : undefined;
  return {
    report: current?.report ?? null,
    isLoading: params !== null && current === undefined,
    isFailed: current !== undefined && current.report === null,
  };
}
